import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { router, Link as RouterLink, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { useDebounce, useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';

import { CommunityDisclaimer } from './CommunityDisclaimerModal';
import { CommunityFilterRow } from './CommunityFilterRow';
import { CommunitySortBy } from './CommunitySortBy';
import { CommunityViewTabs, type CommunityViewKey } from './CommunityViewTabs';
import { CommunityWrapper } from './CommunityWrapper';
import { LibraryPageTitle } from './LibraryPageTitle';
import { PinnedLibraryRail, useLibraryRail, usePinnedRail } from './LibraryRail';
import { LibraryRetryNotice } from './LibraryRetryNotice';
import { PostCardSkeleton } from './PostCardSkeleton';
import { getLibraryListPhase, getListBusyProps } from './libraryListState';
import { useLibraryPosts } from '../../hooks/useLibraryPosts';
import { postRouteParams } from '../../model/postRoute';
import { communityPostsSortByForQuery } from '../../model/sort';
import type { LibraryListParams, LibraryPostSummary } from '../../model/types';
import { useLibraryFilters } from '../../state/libraryFiltersStore';
import { CommunityActionBar } from '../actions/CommunityActionBar';
import { ANSWERS_CARD_CLASS, postDateClassName } from '../answers/answersLayout';

import { Highlight } from '@/components/Highlight';
import { ResultsAnnouncer, useResultsAnnouncement } from '@/components/ResultsAnnouncer';
import { TabRootFlatList, useIsTabRoot } from '@/components/TabRootList';
import { AuthCard } from '@/components/authCard';
import { EmptyQueryResults } from '@/components/emptyQueryResults';
import { Heading } from '@/components/heading';
import {
  AskTheLibraryEntry,
  ResearchAbilityProbe,
  useAskTheLibraryEntryVisible,
} from '@/components/libraryChat/AskTheLibraryEntry';
import { PageWebBanner } from '@/components/pageWebBanner';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Divider } from '@/components/ui/divider';
import { FlatList } from '@/components/ui/flat-list';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { RefreshControl } from '@/components/ui/refresh-control';
import { SearchField } from '@/components/ui/search-field';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { type SortOption } from '@/constants/community';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useAnnouncementSurface } from '@/hooks/useAnnouncements';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useColors } from '@/hooks/useColors';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';
import { useToaster } from '@/hooks/useToaster';
import { getCancerTypesGroupedQueryOptions } from '@/services/getCancerTypesV2';
import {
  trackCommunityDataRequested,
  trackCommunitySearchResultOpened,
  trackCommunityTotalPostsViewed,
  trackCommunityViewed,
} from '@/util/analyticsEvents';

/**
 * How long the search box waits after the last keystroke before querying.
 *
 * 200ms is Algolia's recommended delay for search-as-you-type; they note that
 * anything over 300ms starts to degrade the experience. Don't raise this to
 * clean up analytics — the keystroke series is collapsed by searchSessionId
 * below, not by making the search itself slower.
 */
const SEARCH_DEBOUNCE_MS = 200;

/** How long a refresh runs before the search spinner and the dimming appear. */
const REFRESH_INDICATOR_DELAY_MS = 250;

/**
 * How long the search box sits untouched before the next keystroke counts as a
 * new search rather than a refinement of the current one.
 *
 * `Community data requested` fires once per resolved request, so a search typed
 * as `l` → `lu` → `lung` reports three times. Over the 12 months to Aug 2026
 * that made 13,855 events out of 1,366 real searches, a 10x overstatement, and
 * any report on search demand had to reconstruct the keystroke series in
 * Snowflake first.
 *
 * Rather than guess in the client which keystroke ended a search, every event
 * now carries the id of the typing session it belongs to. The metric is
 * `count(distinct searchSessionId)`, so what counts as one search is a
 * condition in SQL that can be retuned over historic data, instead of a
 * threshold frozen into whatever app version a user happens to be running.
 * Keeping every request also keeps the refinement path, and a query that had to
 * be refined is the signal that the library did not answer the first attempt.
 *
 * 30s only has to be longer than a pause for thought, since a session also ends
 * as soon as the box is cleared. Measured over 12,486 consecutive keystroke
 * pairs the median gap is 299ms and the 90th percentile is 4.3s, so people
 * pause while composing a clinical question but not for anything like this
 * long. Blur deliberately does not end a session: on mobile the keyboard
 * dismisses on every scroll through the results, which would split one search
 * in two.
 */
const SEARCH_SESSION_IDLE_MS = 30_000;

/**
 * A web post card opens the post, so it answers the pointer and the keyboard: a stronger border
 * and the Directory expert card's `shadow-hard-1` on hover, and the app's `indicator-info` ring
 * while anything inside it has keyboard focus.
 */
const POST_CARD_INTERACTIVE_CLASS =
  'hover:border-outline-300 hover:shadow-hard-1 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-indicator-info';

export type PostProps = {
  item: LibraryPostSummary;
  searchInput: string;
  sortBy: SortOption;
  cancerTypeId?: number;
  /** The card is in the Saved view, so its post steps through saved posts. */
  saved?: boolean;
  position: number;
  blocked: boolean;
  onBlockedPress: () => void;
  onOpen?: (position: number) => void;
  /** Draws the post as a white bordered card with hover and keyboard focus states. Web only. */
  framed?: boolean;
};

/**
 * Community post item with position-based navigation link.
 *
 * Passes 1-based position index to post detail for reliable prev/next navigation.
 */
export function Post({
  item,
  searchInput,
  sortBy,
  cancerTypeId,
  saved = false,
  position,
  blocked,
  onBlockedPress,
  onOpen,
  framed = false,
}: PostProps) {
  const title = item.displayTitle;

  const body = item.displayExcerpt;

  const publishedAtDate = item?.publishedAt
    ? format(new Date(item?.publishedAt), 'MM/dd/yyyy')
    : '';

  return (
    <Card
      size="md"
      variant="ghost"
      className={twJoin(
        'w-full flex-col gap-4',
        framed && [ANSWERS_CARD_CLASS, POST_CARD_INTERACTIVE_CLASS]
      )}>
      <HStack className="items-center justify-between">
        <Badge action="info" size="md">
          <BadgeText className="text-info-700">
            {item.cancerType?.shortLabel || item.cancerType?.label}
          </BadgeText>
        </Badge>

        <Text size="sm" className={postDateClassName(framed)}>
          {publishedAtDate}
        </Text>
      </HStack>

      {blocked ? (
        <Pressable className="w-full flex-col" onPress={onBlockedPress}>
          <Heading size="h2-md" className="prose prose-lg mb-2">
            <Highlight rawHTML={title} />
          </Heading>

          <Text size="md" className="w-full font-body text-base leading-relaxed">
            <Highlight rawHTML={body} />
          </Text>
        </Pressable>
      ) : (
        <RouterLink
          href={{
            pathname: isWeb ? '/community/[id]' : '/(secure)/(tabs)/(community)/community/[id]',
            params: postRouteParams({
              id: item.id,
              searchInput,
              sortBy,
              cancerTypeId,
              saved,
              position,
            }),
          }}
          onPress={() => onOpen?.(position)}
          asChild>
          <Pressable className="w-full flex-col">
            <Heading size="h2-md" className="prose prose-lg mb-2">
              <Highlight rawHTML={title} />
            </Heading>

            <Text size="md" className="w-full font-body text-base leading-relaxed">
              <Highlight rawHTML={body} />
            </Text>
          </Pressable>
        </RouterLink>
      )}

      <CommunityActionBar
        item={item}
        searchInput={searchInput}
        sortBy={sortBy}
        cancerTypeId={cancerTypeId}
      />
    </Card>
  );
}

export function Community() {
  const flatListRef = useRef<FlatList>(null);

  const isTabRoot = useIsTabRoot();

  const params = useLocalSearchParams<{
    'saved-posts'?: 'true';
    'cancer-type-id'?: string;
    q?: string;
    sortBy?: string;
  }>();

  const cancerTypeIdString = params['cancer-type-id'] ?? '';

  const cancerTypeIdNumber = Number(cancerTypeIdString);

  const cancerTypeIdParam = !isNaN(cancerTypeIdNumber) ? cancerTypeIdNumber : 0;

  const rawSortByParam = params.sortBy as SortOption | undefined;

  const sortByParam = params['saved-posts'] === 'true' ? undefined : rawSortByParam;

  const [sortBy, setSortBy] = useState<SortOption>(sortByParam || 'newest');

  const cancerTypeId = useLibraryFilters((state) => state.cancerTypeId);

  const setCancerTypeId = useLibraryFilters((state) => state.setCancerTypeId);

  const searchQueryParam = params.q ?? '';

  const searchInput = useLibraryFilters((state) => state.searchQuery);

  const setSearchInput = useLibraryFilters((state) => state.setSearchQuery);

  const [searchQuery, setSearchQuery] = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  /**
   * The typing session every `Community data requested` event is stamped with,
   * so the keystroke series that makes up one search can be collapsed downstream
   * with `count(distinct searchSessionId)`. `searchSequence` orders the series
   * within a session, which is what makes the refinement path readable.
   */
  const searchSessionIdRef = useRef(uuidv4());

  const searchSequenceRef = useRef(0);

  const lastReportedRequestRef = useRef<string | null>(null);

  /**
   * The search that produced the list on screen, as last reported. A result
   * opened from the list is credited to this, not to the live session id: the
   * session rotates after a pause, so a reader who studies the results before
   * opening one would otherwise get an id no search event ever carried. Null
   * while the list is unfiltered.
   */
  const displayedSearchRef = useRef<{ searchSessionId: string; searchSequence: number } | null>(
    null
  );

  const endSearchSession = useCallback(() => {
    searchSessionIdRef.current = uuidv4();

    searchSequenceRef.current = 0;

    // Cleared with the session, so an identical search in a new session reports
    // again rather than being taken for a duplicate of the previous one.
    lastReportedRequestRef.current = null;
  }, []);

  const scheduleSearchSessionEnd = useDebouncedCallback(endSearchSession, SEARCH_SESSION_IDLE_MS);

  const savedPostsParam = params?.['saved-posts'] === 'true';

  const showSavedPosts = useLibraryFilters((state) => state.showSavedPosts);

  const setShowSavedPosts = useLibraryFilters((state) => state.setShowSavedPosts);

  const [totalPostsViewed, setTotalPostsViewed] = useState(0);

  const debouncedTotalPostsViewed = useDebouncedCallback(
    (info: { viewableItems: { index: number }[] }) => {
      const lastViewableItemIndex = info?.viewableItems?.[info?.viewableItems?.length - 1]?.index;

      const value = lastViewableItemIndex != null ? lastViewableItemIndex + 1 : 0;

      if (value > totalPostsViewed) {
        setTotalPostsViewed(value);

        trackCommunityTotalPostsViewed({ value, searchQuery, cancerTypeId, showSavedPosts });
      }
    },
    500
  );

  const resetAllCommunityQueryState = useCallback(() => {
    setCancerTypeId(0);

    setSearchInput('');

    setSearchQuery('');

    setShowSavedPosts(false);

    setTotalPostsViewed(0);
  }, []);

  const {
    userId,
    needsNpiVerification,
    isUnauthenticated,
    isLoading: isAccountLoading,
  } = useAuthStatus();

  const blocked = isUnauthenticated || needsNpiVerification;

  // NOTE this pattern handles deep links that terminate in this screen
  useFocusEffect(
    useCallback(() => {
      if (cancerTypeIdParam && cancerTypeIdParam !== cancerTypeId) {
        setCancerTypeId(cancerTypeIdParam);
      }

      if (searchQueryParam && searchQueryParam !== searchInput) {
        setSearchInput(searchQueryParam);

        setSearchQuery(searchQueryParam);
      }

      // NOTE we don't want to lock the saved posts if the user is not logged in
      if (!isUnauthenticated && savedPostsParam && savedPostsParam !== showSavedPosts) {
        setShowSavedPosts(savedPostsParam);
      }
    }, [userId])
  );

  // NOTE this pattern handles state changes in this screen and the communityWrapper
  useFocusEffect(
    useCallback(() => {
      if (cancerTypeId !== cancerTypeIdParam) {
        router.setParams({ 'cancer-type-id': cancerTypeId ? cancerTypeId : undefined });
      }

      if (searchInput !== searchQueryParam) {
        router.setParams({ q: searchInput ? searchInput : undefined });
      }

      if (showSavedPosts !== savedPostsParam) {
        router.setParams({
          'saved-posts': showSavedPosts ? 'true' : undefined,
          // Clear sortBy whenever saved view is active
          ...(showSavedPosts ? { sortBy: undefined } : {}),
        });
      }

      // Only sync sortBy when not in saved view
      if (!showSavedPosts && sortBy !== (sortByParam || 'newest')) {
        router.setParams({ sortBy: sortBy !== 'newest' ? sortBy : undefined });
      }
    }, [
      cancerTypeId,
      cancerTypeIdParam,
      searchInput,
      searchQuery,
      searchQueryParam,
      showSavedPosts,
      savedPostsParam,
      sortBy,
      sortByParam,
    ])
  );

  // Declares this screen as one where an announcement belongs. The banner itself
  // renders from the secure layout, stacked with the promo.
  useAnnouncementSurface();

  const { theme } = useColors();

  const { bottom } = useSafeAreaInsets();

  const {
    data: cancerTypeData,
    isLoading: cancerTypeIsLoading,
    isError: cancerTypeIsError,
  } = useQuery(getCancerTypesGroupedQueryOptions());

  const cancerTypeOptions = useMemo(
    () =>
      (cancerTypeData?.cancerTypes ?? []).map((cancerType) => ({
        value: String(cancerType.id),
        label: cancerType.label,
        shortLabel: cancerType.shortLabel,
        synonyms: cancerType.synonyms ?? [],
        parentValue: cancerType.parentId == null ? null : String(cancerType.parentId),
        count: cancerType.postCount,
      })),
    [cancerTypeData]
  );

  const libraryListParams = useMemo(
    (): LibraryListParams => ({
      cancerTypeId: cancerTypeId ? Number(cancerTypeId) : undefined,
      includeDescendants: true,
      q: searchQuery ? searchQuery : undefined,
      saved: showSavedPosts ? true : undefined,
      sortBy: communityPostsSortByForQuery(showSavedPosts, sortBy),
    }),
    [cancerTypeId, searchQuery, showSavedPosts, sortBy]
  );

  const {
    data: communityPostsData,
    refetch: communityPostsRefetch,
    fetchNextPage: communityPostsFetchNextPage,
    hasNextPage: communityPostsHasNextPage,
    isFetching: communityPostsIsFetching,
    isFetchingNextPage: communityPostsIsFetchingNextPage,
    isLoading: communityPostsIsLoading,
    isError: communityPostsIsError,
    error: communityPostsError,
    errorUpdatedAt: communityPostsErrorUpdatedAt,
    isSuccess: communityPostsIsSuccess,
    isPlaceholderData: communityPostsIsPlaceholderData,
    status: communityPostsStatus,
    fetchStatus: communityPostsFetchStatus,
    failureCount: communityPostsFailureCount,
    failureReason: communityPostsFailureReason,
  } = useLibraryPosts(libraryListParams);

  const communityPosts = useMemo(
    () =>
      (communityPostsData?.pages?.flatMap?.((page) =>
        page?.posts ? page.posts : []
      ) as LibraryPostSummary[]) || [],
    [communityPostsData?.pages]
  );

  /**
   * The hit count for the whole query, which is what makes a search the library
   * cannot answer visible. `communityPosts.length` is only the posts paged in so
   * far, so it grows as the user scrolls and reads as a healthy result set even
   * for a query that matched almost nothing.
   *
   * The route returns `totalPosts` but `communityPostsResponseSchemas` in the
   * api does not declare it, so it is missing from the generated types. Read
   * defensively until that schema is corrected.
   */
  const totalPosts = (communityPostsData?.pages?.[0] as { totalPosts?: number } | undefined)
    ?.totalPosts;

  // A search ends when the box is cleared or sits idle, and the next keystroke
  // after that opens a new session.
  useEffect(() => {
    if (!searchInput) {
      scheduleSearchSessionEnd.cancel();

      endSearchSession();

      return;
    }

    scheduleSearchSessionEnd();
  }, [searchInput, scheduleSearchSessionEnd, endSearchSession]);

  useEffect(() => {
    // Wait for the in-flight request too, not just a past success. isSuccess
    // stays true across refetches, so reporting on it alone can pair the new
    // query string with the previous query's resultCount.
    if (!communityPostsIsSuccess || communityPostsIsFetching) {
      return;
    }

    if (!(cancerTypeId || searchQuery || showSavedPosts)) {
      displayedSearchRef.current = null;

      return;
    }

    // Paging through results refetches without changing the query, and that is
    // the same search rather than another one.
    const requestKey = `${searchQuery}|${cancerTypeId}|${showSavedPosts}`;

    if (lastReportedRequestRef.current === requestKey) {
      return;
    }

    lastReportedRequestRef.current = requestKey;

    searchSequenceRef.current += 1;

    trackCommunityDataRequested({
      cancerTypeId,
      searchQuery,
      showSavedPosts,
      resultCount: totalPosts,
      searchSessionId: searchSessionIdRef.current,
      searchSequence: searchSequenceRef.current,
    });

    displayedSearchRef.current = {
      searchSessionId: searchSessionIdRef.current,
      searchSequence: searchSequenceRef.current,
    };
  }, [
    communityPostsIsSuccess,
    communityPostsIsFetching,
    searchQuery,
    cancerTypeId,
    showSavedPosts,
    totalPosts,
  ]);

  const listPhase = getLibraryListPhase({
    status: communityPostsStatus,
    fetchStatus: communityPostsFetchStatus,
    failureCount: communityPostsFailureCount,
    hasPosts: communityPosts.length > 0,
    isPlaceholderData: communityPostsIsPlaceholderData,
  });

  // First load only, which the skeleton cards already say. A refresh has its own, delayed signal.
  const isLoading = communityPostsIsLoading || cancerTypeIsLoading || listPhase === 'loading';

  const isError = communityPostsIsError || cancerTypeIsError || listPhase === 'error';

  // A new query is replacing the posts on screen, which are the previous query's.
  const isRefreshingNow =
    communityPostsIsPlaceholderData && communityPostsIsFetching && communityPosts.length > 0;

  // Not shown for a response under 250ms, so a fast one does not flicker.
  const isRefreshing = useDelayedFlag(isRefreshingNow, REFRESH_INDICATOR_DELAY_MS);

  const reduceMotion = useReducedMotion();

  const resultsAnnouncement = useResultsAnnouncement({
    queryKey: `${searchQuery}|${cancerTypeId}|${sortBy}|${showSavedPosts}`,
    isSettled: communityPostsIsSuccess && !communityPostsIsPlaceholderData,
    isError: communityPostsIsError,
    resultCount: totalPosts ?? communityPosts.length,
  });

  const toast = useToaster();

  const { requestAccess } = useAbilityGate();

  /**
   * Same behavior the sidebar's Saved link had: entering Saved drops the cancer type and the
   * keyword search, since neither applies there. Ignored for a reader who cannot read posts,
   * who is asked for access instead.
   */
  const handleViewChange = useCallback(
    (view: CommunityViewKey) => {
      if (blocked) {
        requestAccess();

        return;
      }

      if (view === 'saved') {
        setCancerTypeId(0);

        setSearchQuery('');

        setSearchInput('');
      }

      setShowSavedPosts(view === 'saved');
    },
    [blocked, requestAccess, setCancerTypeId, setSearchQuery, setSearchInput, setShowSavedPosts]
  );

  useFocusEffect(
    useCallback(() => {
      if (communityPostsData) {
        communityPostsRefetch();
      }
    }, [showSavedPosts])
  );

  useFocusEffect(
    useCallback(() => {
      trackCommunityViewed();
    }, [])
  );

  useEffect(() => {
    if (isError) {
      toast.showError({
        title: 'There was an error loading the community. Please try again.',
      });
    }
  }, [isError]);

  /**
   * The list body when there is nothing to show, which is three different
   * situations a reader should be able to tell apart: still fetching, refused,
   * and genuinely empty. A skeleton left standing after a failure claims the
   * posts are still coming, so the failure gets its own surface with a way out.
   */
  const renderListEmpty = () => {
    if (isLoading) {
      return <PostCardSkeleton framed={isWebPage} />;
    }

    if (isError) {
      return (
        <LibraryRetryNotice
          error={communityPostsError ?? communityPostsFailureReason}
          failedAt={communityPostsErrorUpdatedAt}
          onRetry={() => communityPostsRefetch()}
          surface="list"
        />
      );
    }

    return (
      <EmptyQueryResults
        {...(showSavedPosts
          ? {
              title: 'Nothing to see here!',
              text: 'When you save an inquiry, you can refer back to it here',
              cta: 'View Inquiry Library',
            }
          : {})}
        onCtaPress={resetAllCommunityQueryState}
      />
    );
  };

  const researchEntryVisible = useAskTheLibraryEntryVisible();

  const [canReadResearch, setCanReadResearch] = useState(false);

  const rail = useLibraryRail({
    showSavedPosts,
    canReadResearch,
    isResolvingAbility: isAccountLoading,
  });

  const pinned = usePinnedRail(rail.showRail);

  /*
   * On the web page every row (title, tabs, search, filters, posts) shares one box, so they
   * share one left and right edge. With the rail it is 1248px wide with 24px gutters, a 1200px
   * grid: the main column takes what is left of a 344px reservation (856px at full width), and
   * the rail is a 312px column set 32px past it. Without the rail the page is the single
   * max-w-5xl column it has always been. Tab roots and native keep their own inner padding.
   */
  const isWebPage = isWeb && !isTabRoot;

  let contentWidth = twJoin('w-full', !isTabRoot && 'mx-auto max-w-5xl');

  if (isWebPage && rail.reservesRail) {
    contentWidth = 'w-full mx-auto max-w-[1248px] pl-4 pr-4 md:pl-6 md:pr-6 lg:pr-[368px]';
  } else if (isWebPage) {
    contentWidth = 'w-full mx-auto max-w-5xl px-4 md:px-6';
  }

  const postsList = (
    <TabRootFlatList
      ref={flatListRef}
      onViewableItemsChanged={debouncedTotalPostsViewed as any}
      className={twJoin('w-full', isTabRoot && 'flex-1')}
      /*
       * Always scrollable on a tab root, as the other tabs' lists are. The native header
       * collapses its large title against this list, and it was the one list there that
       * mounted unscrollable while its posts loaded.
       */
      scrollEnabled={isTabRoot || communityPosts?.length > 0}
      keyboardDismissMode="on-drag"
      {...getListBusyProps(isLoading || isRefreshingNow)}
      showsVerticalScrollIndicator={false}
      onScroll={rail.showRail ? pinned.update : undefined}
      scrollEventThrottle={rail.showRail ? 16 : undefined}
      /*
       * Not sticky on a tab root. The header there carries the announcement banner and the
       * segment control, which would reappear over the list on every scroll up.
       */
      stickyHeaderIndices={!isWeb && !isTabRoot ? [0] : undefined}
      stickyHeaderHiddenOnScroll
      ListHeaderComponentClassName="w-full"
      ListHeaderComponent={
        <>
          {isWeb && isTabRoot ? (
            <PageWebBanner
              header="Previously Asked Questions and Answers"
              subtext="Shared in anonymized form for learning and reference."
            />
          ) : null}
          <View className={contentWidth}>
            <ResultsAnnouncer message={resultsAnnouncement} testID="library-results-announcer" />

            {isWebPage ? <LibraryPageTitle /> : null}
            {!isTabRoot ? (
              <CommunityViewTabs showSavedPosts={showSavedPosts} onChange={handleViewChange} />
            ) : null}
            <View ref={pinned.anchorRef} className="w-full">
              <Pressable
                className={twJoin(
                  'w-full cursor-default flex-col gap-4 pb-4 pt-4 md:pb-5',
                  isWebPage ? 'md:pt-4' : 'bg-background-0 px-4 md:pt-8 lg:px-6'
                )}
                onPress={() => {
                  if (isUnauthenticated) {
                    requestAccess();
                  }
                }}>
                {!showSavedPosts ? (
                  <View className="w-full flex-col gap-4 lg:flex-row lg:items-center lg:gap-3">
                    <SearchField
                      label="Search inquiries"
                      placeholder="Search by keyword"
                      value={searchInput}
                      onChangeText={setSearchInput}
                      onSubmitEditing={() => setSearchQuery(searchInput)}
                      onClear={() => {
                        setSearchInput('');

                        setSearchQuery('');
                      }}
                      busy={isRefreshing && !reduceMotion}
                      className={twJoin(
                        'bg-background-0',
                        isWebPage && 'h-11 lg:min-w-0 lg:flex-1'
                      )}
                    />

                    {rail.showResearchInline ? <AskTheLibraryEntry /> : null}

                    <CommunityFilterRow
                      compactSort={isWebPage && rail.isWide}
                      showSavedPosts={showSavedPosts}
                      sortBy={sortBy}
                      onSortChange={setSortBy}
                      isLoading={cancerTypeIsLoading}
                      cancerTypeId={cancerTypeId}
                      onChange={setCancerTypeId}
                      options={cancerTypeOptions}
                      needsNpiVerification={needsNpiVerification}
                      textColor={theme.colors.text}
                      bottomInset={bottom}
                    />
                  </View>
                ) : null}

                {!showSavedPosts && searchInput?.length > 0 && communityPosts?.length > 0 ? (
                  <Text
                    size="sm"
                    className={isWebPage ? 'text-typography-600' : 'text-typography-500'}>
                    Search results:
                  </Text>
                ) : null}
              </Pressable>
            </View>
          </View>
        </>
      }
      data={communityPosts}
      keyExtractor={(_, index) => index.toString()}
      contentContainerClassName="pb-10"
      renderItem={({ item, index }) => (
        <View className={twJoin(contentWidth, isRefreshing && 'opacity-60')}>
          <Post
            item={item}
            searchInput={searchInput}
            sortBy={sortBy}
            cancerTypeId={cancerTypeId}
            saved={showSavedPosts}
            position={index + 1}
            blocked={blocked}
            framed={isWebPage}
            onBlockedPress={requestAccess}
            onOpen={(position) => {
              if (displayedSearchRef.current) {
                trackCommunitySearchResultOpened({
                  postId: item.id,
                  listPosition: position,
                  ...displayedSearchRef.current,
                });
              }
            }}
          />
        </View>
      )}
      ItemSeparatorComponent={() =>
        isWebPage ? (
          <View className="h-4" />
        ) : (
          <View className={contentWidth}>
            <Divider />
          </View>
        )
      }
      onEndReachedThreshold={0.8}
      onEndReached={() =>
        !isUnauthenticated &&
        !communityPostsIsPlaceholderData &&
        !needsNpiVerification &&
        communityPostsHasNextPage
          ? communityPostsFetchNextPage()
          : null
      }
      ListEmptyComponent={<View className={contentWidth}>{renderListEmpty()}</View>}
      ListFooterComponent={
        <VStack className={twJoin(contentWidth, 'mb-8', !isWebPage && 'px-4 md:px-0 lg:px-6')}>
          {isWeb && isUnauthenticated ? (
            <AuthCard
              text="Sign in or create an account to see the full Inquiry Library"
              className="mt-3"
            />
          ) : null}
          {communityPostsIsFetchingNextPage ? <Spinner size="large" /> : null}
        </VStack>
      }
      refreshControl={
        <RefreshControl
          refreshing={communityPostsIsFetchingNextPage}
          onRefresh={() => {
            communityPostsRefetch();
          }}
        />
      }
    />
  );

  /*
   * Bare on a tab root. The wrapper lays out a web desktop sidebar beside the list, and on
   * native that row put a second scroll view ahead of this one, which is the one the large
   * title has to collapse against. Only the disclaimer carries over.
   */
  if (isTabRoot) {
    return (
      <>
        {postsList}

        <CommunityDisclaimer />
      </>
    );
  }

  const researchProbe = researchEntryVisible ? (
    <ResearchAbilityProbe onResolve={setCanReadResearch} />
  ) : null;

  if (rail.showRail) {
    return (
      <CommunityWrapper>
        {researchProbe}

        <View ref={pinned.overlayRef} className="relative w-full flex-1">
          {postsList}

          <PinnedLibraryRail railRef={pinned.railRef} />
        </View>
      </CommunityWrapper>
    );
  }

  return (
    <CommunityWrapper>
      {researchProbe}

      {postsList}
    </CommunityWrapper>
  );
}
